"use client";

import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import type { BannerVideoCardProps } from "./BannerVideoCard.types";
import VideoCardSkeleton from "../../skeletons/VideoCardSkeleton/VideoCardSkeleton";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";

import "./BannerVideoCard.css";

const BannerVideoCard = ({
  title,
  videoSrc,
  thumbnail,
  thumbnailTarget,
  thumbnailKind = "thumbnail",
  thumbnailFieldName = "thumbnail",
  showIntroLabel = true,
  className = "",
  onClick,
}: BannerVideoCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // normalize nullable → undefined
  const src = videoSrc ?? undefined;
  const poster = thumbnail ?? undefined;

  const handleTogglePlay = () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      void v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handleEnded = () => setIsPlaying(false);
    v.addEventListener("ended", handleEnded);

    return () => v.removeEventListener("ended", handleEnded);
  }, []);

  return (
    <div
      className={`banner-card-wrapper ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div
        className="banner-video-container"
        onClick={(e) => {
          // prevent bubbling to wrapper when user toggles via container area
          e.stopPropagation();
          handleTogglePlay();
        }}
      >
        {!isLoaded && (
          <>
            <VideoCardSkeleton variant="banner" />

            {/* ✅ CDN thumbnail warmup */}
            {poster && thumbnailTarget && (
              <div style={{ display: "none" }} aria-hidden="true">
                <PrivateCdnImage
                  src={poster}
                  placeholderSrc={null}
                  alt="poster preloader"
                  target={thumbnailTarget}
                  kind={thumbnailKind as any}
                  fieldName={thumbnailFieldName as any}
                  rootMargin="900px"
                  threshold={0.01}
                  failOpen
                  imgLoading="eager"
                  fetchPriority="high"
                  onLoad={() => setIsLoaded(true)}
                  onError={() => {
                    // keep skeleton until video loads
                  }}
                />
              </div>
            )}

            {/* Optional fallback if target wasn't passed */}
            {poster && !thumbnailTarget && (
              <img
                src={poster}
                alt="poster preloader"
                style={{ display: "none" }}
                onLoad={() => setIsLoaded(true)}
              />
            )}
          </>
        )}

        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          playsInline
          preload="metadata"
          className={`banner-video-element ${isLoaded ? "loaded" : "loading"}`}
          onLoadedData={() => setIsLoaded(true)}
        />

        <div className="banner-title-overlay">
          <h3 className="banner-title">{title}</h3>
        </div>

        <div className="banner-bottom-bar">
          {showIntroLabel && <div className="intro-label">Intro</div>}

          <button
            className="banner-play-button"
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePlay();
            }}
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannerVideoCard;
